/**
 * Active facet chips — “Refine by” row with per-value remove and clear all.
 */
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  SearchAppContext,
  SearchAppDispatchContext,
} from '../../context/search-app/search-app-context';
import {
  CLEAR_FILTERS,
  SET_FACET,
} from '../../context/search-app/search-app-actions';
import { hasActiveFacets } from '../../context/search-app/has-active-facets';
import { facetIdsEqual } from '../../context/search-app/facet-id';
import { DEAKIN_TAGS_FACET_ID } from '../../funnelback/deakin-config';
import { resolveFacetValueLabel, facetValuesMatch } from './resolve-facet-value-label';
import { truncateWords } from './truncate-words';

const MOBILE_CHIP_LABEL_MAX_WORDS = 4;
const MOBILE_MEDIA_QUERY = '(width < 48rem)';

export const BASE_CLASS = 'selected-facet-chips';

type SelectedFacetChip = {
  facetId: string;
  value: string;
  label: string;
};

function buildSelectedFacetChips(
  selectedFacets: Record<string, string[]>,
  funnelbackFacets: unknown[],
): SelectedFacetChip[] {
  const chips: SelectedFacetChip[] = [];
  for (const [facetId, values] of Object.entries(selectedFacets)) {
    if (facetIdsEqual(facetId, DEAKIN_TAGS_FACET_ID)) {
      continue;
    }
    for (const value of values) {
      if (!value.trim()) {
        continue;
      }
      chips.push({
        facetId,
        value,
        label: resolveFacetValueLabel(facetId, value, funnelbackFacets),
      });
    }
  }
  return chips;
}

const SelectedFacetChips = () => {
  const { selectedFacets, funnelbackFacets } = useContext(SearchAppContext);
  const { dispatch } = useContext(SearchAppDispatchContext);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const sync = () => setIsMobile(mediaQuery.matches);
    sync();
    mediaQuery.addEventListener('change', sync);
    return () => mediaQuery.removeEventListener('change', sync);
  }, []);

  const chips = useMemo(
    () => buildSelectedFacetChips(selectedFacets, funnelbackFacets),
    [selectedFacets, funnelbackFacets],
  );

  const removeChip = useCallback(
    (facetId: string, value: string) => {
      const current = selectedFacets[facetId] ?? [];
      const nextValues = current.filter(
        (entry) => !facetValuesMatch(entry, value),
      );
      dispatch({
        type: SET_FACET,
        payload: { facetId, values: nextValues },
      });
    },
    [dispatch, selectedFacets],
  );

  const clearAll = useCallback(() => {
    dispatch({ type: CLEAR_FILTERS });
  }, [dispatch]);

  if (!hasActiveFacets(selectedFacets) || chips.length === 0) {
    return null;
  }

  return (
    <div className={BASE_CLASS}>
      <div className={`${BASE_CLASS}__row`}>
        <span className={`${BASE_CLASS}__heading`}>Refine by</span>
        <ul className={`${BASE_CLASS}__list`} aria-label="Selected filters">
          {chips.map((chip) => {
            const displayLabel = isMobile
              ? truncateWords(chip.label, MOBILE_CHIP_LABEL_MAX_WORDS)
              : chip.label;
            const isTruncated = displayLabel !== chip.label;

            return (
            <li key={`${chip.facetId}:${chip.value}`} className="selected-facet-chip">
              <span
                className="selected-facet-chip__label filter-button-label"
                title={isTruncated ? chip.label : undefined}
              >
                {displayLabel}
              </span>
              <button
                type="button"
                className="selected-facet-chip__remove"
                aria-label={`Remove ${chip.label}`}
                onClick={() => removeChip(chip.facetId, chip.value)}
              >
                <span aria-hidden="true">×</span>
              </button>
            </li>
            );
          })}
          <li className={`${BASE_CLASS}__clear-all-item`}>
            <button
              type="button"
              className={`${BASE_CLASS}__clear-all`}
              onClick={clearAll}
            >
              Clear all
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default SelectedFacetChips;
